import React, { useState, useEffect } from 'react';
import { 
  Coins, 
  Wallet, 
  Building2, 
  ShieldCheck, 
  Store, 
  Sparkles, 
  ArrowLeft 
} from 'lucide-react';
import { 
  CashbackCompany, 
  CashbackRequest, 
  CashbackTransaction, 
  CashbackCampaign, 
  CashbackMission, 
  CashbackReferral,
  CashbackMerchantAccessRequest,
  CashbackBannerConfig,
  INITIAL_CASHBACK_COMPANIES,
  INITIAL_CASHBACK_REQUESTS,
  INITIAL_CASHBACK_TRANSACTIONS,
  INITIAL_CASHBACK_CAMPAIGNS,
  INITIAL_CASHBACK_MISSIONS,
  INITIAL_CASHBACK_MERCHANT_REQUESTS,
  INITIAL_CASHBACK_BANNER_CONFIG
} from './CashbackTypes';
import { CashbackClientWallet } from './CashbackClientWallet';
import { CashbackMerchantPanel } from './CashbackMerchantPanel';
import { CashbackAdminPanel } from './CashbackAdminPanel';
import { CashbackDirectoryView } from './CashbackDirectoryView';
import { CashbackMerchantRequestView } from './CashbackMerchantRequestView';

interface CashbackMainPageProps {
  currentUser?: any;
  apiUrl?: string;
  onNavigateHome?: () => void;
  onLoginRequest?: () => void;
  defaultRoleView?: 'directory' | 'wallet' | 'merchant' | 'admin';
}

export const CashbackMainPage: React.FC<CashbackMainPageProps> = ({
  currentUser,
  apiUrl,
  onNavigateHome,
  onLoginRequest,
  defaultRoleView = 'directory'
}) => {
  const [activeRoleView, setActiveRoleView] = useState<'directory' | 'wallet' | 'merchant' | 'admin'>(defaultRoleView);

  useEffect(() => {
    if (defaultRoleView) {
      setActiveRoleView(defaultRoleView);
    }
  }, [defaultRoleView]);

  // State with LocalStorage Persistence
  const [companies, setCompanies] = useState<CashbackCompany[]>(() => {
    const saved = localStorage.getItem('cashback_companies');
    return saved ? JSON.parse(saved) : INITIAL_CASHBACK_COMPANIES;
  });

  const [requests, setRequests] = useState<CashbackRequest[]>(() => {
    const saved = localStorage.getItem('cashback_requests');
    return saved ? JSON.parse(saved) : INITIAL_CASHBACK_REQUESTS;
  });

  const [transactions, setTransactions] = useState<CashbackTransaction[]>(() => {
    const saved = localStorage.getItem('cashback_transactions');
    return saved ? JSON.parse(saved) : INITIAL_CASHBACK_TRANSACTIONS;
  });

  const [campaigns, setCampaigns] = useState<CashbackCampaign[]>(() => {
    const saved = localStorage.getItem('cashback_campaigns');
    return saved ? JSON.parse(saved) : INITIAL_CASHBACK_CAMPAIGNS;
  });

  const [merchantAccessRequests, setMerchantAccessRequests] = useState<CashbackMerchantAccessRequest[]>(() => {
    const saved = localStorage.getItem('cashback_merchant_requests');
    return saved ? JSON.parse(saved) : INITIAL_CASHBACK_MERCHANT_REQUESTS;
  });

  const [bannerConfig, setBannerConfig] = useState<CashbackBannerConfig>(() => {
    const saved = localStorage.getItem('cashback_banner_config');
    return saved ? JSON.parse(saved) : INITIAL_CASHBACK_BANNER_CONFIG;
  });

  useEffect(() => {
    localStorage.setItem('cashback_banner_config', JSON.stringify(bannerConfig));
  }, [bannerConfig]);

  const [approvedUsers, setApprovedUsers] = useState<string[]>(() => {
    const saved = localStorage.getItem('cashback_approved_users');
    return saved ? JSON.parse(saved) : ['usr-1', 'usr-2', 'usr-admin', 'admin'];
  });

  const [missions, setMissions] = useState<CashbackMission[]>(INITIAL_CASHBACK_MISSIONS);

  const [referral, setReferral] = useState<CashbackReferral>({
    referral_code: 'REF-' + (currentUser?.id || 'CARLOS88'),
    total_referred: 3,
    total_earned: 30.00,
    referrer_bonus: 10.00,
    referred_bonus: 10.00
  });

  // Selected company for merchant view
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>(companies[0]?.id || 'emp-1');

  // Check if current user is approved merchant or admin
  const isSuperAdmin = currentUser?.role === 'admin' || currentUser?.isSuperAdmin;
  const isMerchantApproved = isSuperAdmin || 
    approvedUsers.includes(currentUser?.id) || 
    approvedUsers.includes(currentUser?.email) || 
    currentUser?.has_cashback_merchant || 
    !!currentUser?.business_id;

  // Save to LocalStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('cashback_companies', JSON.stringify(companies));
  }, [companies]);

  useEffect(() => {
    localStorage.setItem('cashback_requests', JSON.stringify(requests));
  }, [requests]);

  useEffect(() => {
    localStorage.setItem('cashback_transactions', JSON.stringify(transactions));
  }, [transactions]);

  useEffect(() => {
    localStorage.setItem('cashback_campaigns', JSON.stringify(campaigns));
  }, [campaigns]);

  useEffect(() => {
    localStorage.setItem('cashback_merchant_requests', JSON.stringify(merchantAccessRequests));
  }, [merchantAccessRequests]);

  useEffect(() => {
    localStorage.setItem('cashback_approved_users', JSON.stringify(approvedUsers));
  }, [approvedUsers]);

  // Submit merchant request
  const handleMerchantRequestSubmit = (data: {
    company_name: string;
    company_category: string;
    company_city: string;
    cnpj_cpf: string;
    user_phone: string;
    notes: string;
  }) => {
    const newReq: CashbackMerchantAccessRequest = {
      id: `req-m-${Date.now()}`,
      user_id: currentUser?.id || `usr-${Date.now()}`,
      user_name: currentUser?.name || 'Usuário Solicitante',
      user_email: currentUser?.email || 'usuario@exemplo.com',
      user_phone: data.user_phone,
      company_name: data.company_name,
      company_category: data.company_category,
      company_city: data.company_city,
      cnpj_cpf: data.cnpj_cpf,
      notes: data.notes,
      status: 'pending',
      created_at: new Date().toISOString().replace('T', ' ').substring(0, 16)
    };

    setMerchantAccessRequests(prev => [newReq, ...prev]);
  };

  // Approve merchant access request (Super Admin)
  const handleApproveMerchantAccessRequest = (requestId: string) => {
    const req = merchantAccessRequests.find(r => r.id === requestId);
    if (!req) return;

    // Update request status
    setMerchantAccessRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'approved', reviewed_at: new Date().toISOString() } : r));

    // Grant user access
    setApprovedUsers(prev => Array.from(new Set([...prev, req.user_id, req.user_email])));

    // Create Company for Merchant
    const newCompany: CashbackCompany = {
      id: `emp-${Date.now()}`,
      name: req.company_name,
      category: req.company_category,
      logo_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&h=200&fit=crop',
      city_state: req.company_city,
      phone: req.user_phone,
      whatsapp: req.user_phone,
      status: 'active',
      plan: 'pro',
      cross_cashback_enabled: true,
      created_at: new Date().toISOString().split('T')[0],
      settings: {
        type: 'percent',
        default_percent: 10,
        fixed_amount: 0,
        min_purchase: 20,
        max_cashback_per_purchase: 100,
        validity_days: 60
      }
    };

    setCompanies(prev => [newCompany, ...prev]);
    setSelectedMerchantId(newCompany.id);
  };

  const handleRejectMerchantAccessRequest = (requestId: string) => {
    setMerchantAccessRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'rejected', reviewed_at: new Date().toISOString() } : r));
  };

  const handleGrantManualAccess = (userName: string, userEmail: string, companyName: string, category: string) => {
    const mockId = `usr-${Date.now()}`;
    setApprovedUsers(prev => Array.from(new Set([...prev, mockId, userEmail])));

    const newCompany: CashbackCompany = {
      id: `emp-${Date.now()}`,
      name: companyName,
      category: category,
      logo_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&h=200&fit=crop',
      city_state: 'Ribeirão Preto - SP',
      status: 'active',
      plan: 'pro',
      cross_cashback_enabled: true,
      created_at: new Date().toISOString().split('T')[0],
      settings: {
        type: 'percent',
        default_percent: 10,
        fixed_amount: 0,
        min_purchase: 20,
        max_cashback_per_purchase: 100,
        validity_days: 60
      }
    };

    setCompanies(prev => [newCompany, ...prev]);
    alert(`Acesso concedido com sucesso para ${userName} (${companyName})!`);
  };

  // Handlers for Client Wallet
  const handleSubmitReceipt = (data: { business_id: string; amount: number; receipt_number: string; purchase_date: string; photo_url: string }) => {
    const company = companies.find(c => c.id === data.business_id);
    const percent = company?.settings.default_percent || 10;
    const calculated = (data.amount * percent) / 100;

    const newReq: CashbackRequest = {
      id: `req-${Date.now()}`,
      business_id: data.business_id,
      business_name: company?.name || 'Estabelecimento Parceiro',
      user_id: currentUser?.id || 'usr-1',
      user_name: currentUser?.name || 'Carlos Eduardo Silva',
      user_phone: currentUser?.phone || '16992001122',
      receipt_photo: data.photo_url,
      receipt_number: data.receipt_number,
      purchase_amount: data.amount,
      purchase_date: data.purchase_date,
      calculated_cashback: calculated,
      status: 'pending',
      created_at: new Date().toISOString().replace('T', ' ').substring(0, 16)
    };

    setRequests(prev => [newReq, ...prev]);
  };

  const handleUseCashback = (business_id: string, amount: number) => {
    const company = companies.find(c => c.id === business_id);
    const newTx: CashbackTransaction = {
      id: `tx-${Date.now()}`,
      business_id: business_id,
      business_name: company?.name || 'Estabelecimento Parceiro',
      type: 'spend',
      amount: -amount,
      description: `Resgate de Cashback em ${company?.name || 'Loja'}`,
      date: new Date().toISOString().split('T')[0],
      status: 'used'
    };

    setTransactions(prev => [newTx, ...prev]);
  };

  // Handlers for Merchant
  const handleApproveRequest = (requestId: string) => {
    const req = requests.find(r => r.id === requestId);
    if (!req) return;

    setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'approved', reviewed_at: new Date().toISOString() } : r));

    const newTx: CashbackTransaction = {
      id: `tx-${Date.now()}`,
      business_id: req.business_id,
      business_name: req.business_name,
      type: 'earn',
      amount: req.calculated_cashback,
      description: `Cashback Aprovado - Cupom ${req.receipt_number}`,
      date: new Date().toISOString().split('T')[0],
      status: 'active',
      expires_at: new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0]
    };

    setTransactions(prev => [newTx, ...prev]);
  };

  const handleRejectRequest = (requestId: string, reason: string) => {
    setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'rejected', rejection_reason: reason, reviewed_at: new Date().toISOString() } : r));
  };

  const handleBatchApprove = (requestIds: string[]) => {
    requestIds.forEach(id => handleApproveRequest(id));
  };

  const handleSaveCompanyRules = (companyId: string, rules: any) => {
    setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, settings: rules } : c));
  };

  const handleCreateCampaign = (campaign: any) => {
    setCampaigns(prev => [campaign, ...prev]);
  };

  // Handlers for Admin
  const handleToggleCompanyStatus = (companyId: string) => {
    setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, status: c.status === 'active' ? 'blocked' : 'active' } : c));
  };

  const handleUpdateCompanyPlan = (companyId: string, plan: 'free' | 'pro' | 'enterprise') => {
    setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, plan } : c));
  };

  const handleToggleCrossCashback = (companyId: string) => {
    setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, cross_cashback_enabled: !c.cross_cashback_enabled } : c));
  };

  const handleAddCompany = (newCompany: CashbackCompany) => {
    setCompanies(prev => [newCompany, ...prev]);
  };

  const activeMerchantCompany = companies.find(c => c.id === selectedMerchantId) || companies[0];

  // User's existing access request if any
  const existingUserRequest = merchantAccessRequests.find(r => 
    r.user_id === currentUser?.id || (currentUser?.email && r.user_email === currentUser?.email)
  );

  return (
    <div className="min-h-screen bg-slate-100/70 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* TOP BAR / MODE SWITCHER */}
        <div className="bg-white rounded-3xl p-4 border border-slate-200/80 shadow-md flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {onNavigateHome && (
              <button
                onClick={onNavigateHome}
                className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl transition-colors cursor-pointer shrink-0"
                title="Voltar"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <div>
              <h2 className="text-xl md:text-2xl font-black text-emerald-600 tracking-tight flex items-center gap-2">
                <Coins size={22} className="text-emerald-500" /> Ganhe CashBack
              </h2>
            </div>
          </div>

          {/* ROLE SWITCHER BUTTONS */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1.5 rounded-2xl overflow-x-auto w-full md:w-auto shrink-0">
            {[
              { id: 'directory', label: 'Vitrine Pública', icon: Store },
              { id: 'wallet', label: 'Carteira do Cliente', icon: Wallet },
              { id: 'merchant', label: 'Empresa / Lojista', icon: Building2 },
              { id: 'admin', label: 'Admin Cashback SaaS', icon: ShieldCheck }
            ].map(role => {
              const Icon = role.icon;
              const isActive = activeRoleView === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => setActiveRoleView(role.id as any)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold uppercase transition-all shrink-0 cursor-pointer ${
                    isActive
                      ? 'bg-emerald-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-200/80'
                  }`}
                >
                  <Icon size={15} />
                  {role.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* SELECT MERCHANT IF IN MERCHANT VIEW AND APPROVED */}
        {activeRoleView === 'merchant' && isMerchantApproved && (
          <div className="bg-white rounded-2xl p-3 border border-slate-200 flex items-center justify-between gap-3 text-xs font-bold">
            <span className="text-slate-500 uppercase tracking-wider">Alternar Empresa Gerenciada:</span>
            <select
              value={selectedMerchantId}
              onChange={(e) => setSelectedMerchantId(e.target.value)}
              className="bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl font-bold text-slate-800"
            >
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.category})</option>
              ))}
            </select>
          </div>
        )}

        {/* ACTIVE VIEW RENDERING */}
        {activeRoleView === 'directory' && (
          <CashbackDirectoryView
            companies={companies}
            campaigns={campaigns}
            bannerConfig={bannerConfig}
            onSelectCompanyToSubmit={(companyId) => {
              setActiveRoleView('wallet');
            }}
            onOpenWallet={() => setActiveRoleView('wallet')}
          />
        )}

        {activeRoleView === 'wallet' && (
          <CashbackClientWallet
            companies={companies}
            requests={requests}
            transactions={transactions}
            campaigns={campaigns}
            missions={missions}
            referral={referral}
            onSubmitReceipt={handleSubmitReceipt}
            onUseCashback={handleUseCashback}
            currentUser={currentUser}
            onLoginRequest={onLoginRequest}
          />
        )}

        {activeRoleView === 'merchant' && (
          isMerchantApproved ? (
            <CashbackMerchantPanel
              company={activeMerchantCompany}
              requests={requests}
              transactions={transactions}
              campaigns={campaigns}
              onApproveRequest={handleApproveRequest}
              onRejectRequest={handleRejectRequest}
              onBatchApprove={handleBatchApprove}
              onSaveRules={handleSaveCompanyRules}
              onCreateCampaign={handleCreateCampaign}
            />
          ) : (
            <CashbackMerchantRequestView
              currentUser={currentUser}
              existingRequest={existingUserRequest}
              onSubmitRequest={handleMerchantRequestSubmit}
            />
          )
        )}

        {activeRoleView === 'admin' && (
          <CashbackAdminPanel
            companies={companies}
            requests={requests}
            transactions={transactions}
            merchantRequests={merchantAccessRequests}
            bannerConfig={bannerConfig}
            onUpdateBannerConfig={setBannerConfig}
            onToggleCompanyStatus={handleToggleCompanyStatus}
            onUpdateCompanyPlan={handleUpdateCompanyPlan}
            onToggleCrossCashback={handleToggleCrossCashback}
            onAddCompany={handleAddCompany}
            onApproveMerchantRequest={handleApproveMerchantAccessRequest}
            onRejectMerchantRequest={handleRejectMerchantAccessRequest}
            onGrantManualAccess={handleGrantManualAccess}
          />
        )}

      </div>
    </div>
  );
};

